import angular from "angular";
import { AccountingChannel, AccountingTargets, AccountingTargetSettings, Ajax, Configs, ExportMethod, ExternalDialogData } from "@salaxy/core";
import { ExternalDialogConfig, SessionService, SettingsService, UiHelpers } from "../../services";

/**
 * Handles user interfaces for specifying settings for Payment Channels (and other payment related settings).
 */
export class AccountingChannelSettingsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SettingsService", "UiHelpers", "SessionService", "$http", "AjaxNg1"];

  // Function for closing the settings modal in case we need to close the window programmatically
  // e.g. based on the return value fromt the external dialog.
  private closeFn: (string) => any;

  private static channelPaths = {
    [AccountingChannel.Procountor]: {}, // Procountor accounting channel is under construction.
    [AccountingChannel.VismaNetvisor]: {
      settings: "vismanetvisor/settings.html",
      coa: "vismanetvisor/coa",
    },
    [AccountingChannel.VismaFivaldi]: {
      settings: "vismafivaldi/settings.html",
      coa: "vismafivaldi/coa",
    },
  };

  constructor(
    private settingsService: SettingsService,
    private uiHelpers: UiHelpers,
    private sessionService: SessionService,
    private $http: angular.IHttpService,
    private ajax: Ajax
  ) {
  }

  /**
   * Controller initializations
   */
  public $onInit = () => {
    // none
  };

  /**
   * Returns true if the given accounting target item is an API integration type acccounting
   * target.
   * @param item The accounting target item
   * @returns True or false.
   */
  public isApiIntegration(item: any): boolean {
    return (item?.availableMethods ?? []).indexOf(ExportMethod.Api) > -1 && (
        item?.channel === AccountingChannel.Procountor
        || item?.channel === AccountingChannel.VismaNetvisor
        || item?.channel === AccountingChannel.VismaFivaldi
      );
  }

  /** Opens the channel setup dialog in a new window. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public openChannelSetupDialog(target: AccountingTargetSettings, closeFn: (string) => any) {
    if (!target?.channel) {
      return;
    }

    this.closeFn = closeFn;

    this.uiHelpers.showExternalDialog(
      target.id,
      this.getAccountingTargetSettingsUrl(target),
      {},
      this.getExternalDialogConfigByTarget(target)
    )
      .then(res => {
        console.log("Accounting target settings saved, results = ", res);
        // If handling of return data caused the settings to be saved
        if (this.handleExternalDialogReturnData(target, res)) {
          // Set the updated data also to the original target, otherwise the updates are not
          // reflected on the caller e.g. on a settings page.
          target.data = { ...target.data, ...(res.item as AccountingTargetSettings).data };
        }
      });
  }

  /**
   * Loads the chart of accounts from the channel.
   * @param target Accounting target.
   * @param closeFn $close function to call.
   */
  public async configureCoa(target: AccountingTargetSettings, closeFn: (string) => any) {
    this.closeFn = closeFn;
    const coa = await this.loadCoaFromChannel(target);

    if (!coa) {
      // TODO: Should throw/show some error?
      return;
    }

    navigator.clipboard.writeText(coa);
    // Navigate to accounting targets configuration view.
    this.closeFn("close");
    const ownerId = ((await new AccountingTargets(this.ajax).getOData(null)) as any).items.find(v => v.id === target.id)?.owner;

    if (this.isAdmin()) {
      window.location.href = `/AccountingTargetDto/Client/${ownerId}/#/accounting-targets/details/${target.id}`;
    } else {
      window.location.href = `./#/accounting-targets/details/${target.id}@${ownerId}`;
    }
  }

  private async loadCoaFromChannel(target: AccountingTargetSettings): Promise<string> {
    const response = await this.$http.get(this.getCoaUrl(target), { responseType: "json" });
    return Object.keys(response.data).reduce((pre, cur) => pre + cur + ";" + response.data[cur] + "\n", "");
  }

  private getAccountingTargetSettingsUrl(target: AccountingTargetSettings): string {
    const accessToken = this.sessionService.getCurrentToken();
    const url = Configs.current.integrationsServer + "/" + AccountingChannelSettingsController.channelPaths[target.channel].settings;

    return `${url}?accessToken=${accessToken}&accountingTargetId=${target.id}`;
  }

  private getExternalDialogConfigByTarget(target: AccountingTargetSettings): ExternalDialogConfig {
    switch(target.channel) {
      case AccountingChannel.VismaFivaldi:
        return { height: 810 };
      default:
        return {};
    }
  }

  private getCoaUrl(target: AccountingTargetSettings): string {
    const accessToken = this.sessionService.getCurrentToken();
    const url = Configs.current.integrationsServer + "/" + AccountingChannelSettingsController.channelPaths[target.channel].coa;

    return `${url}?accessToken=${accessToken}&accountingTargetId=${target.id}`;
  }

  private handleExternalDialogReturnData(target: AccountingTargetSettings, data: ExternalDialogData<any>): boolean {
    switch(data.action) {
      case "cancel":
        break;
      case "save":
        if (!angular.equals(target.data, (data.item as AccountingTargetSettings).data)){
          this.saveSettings(data.item);
          return true;
        }
        break;
      default:
        return false;
    }
  }

  private saveSettings(settings: AccountingTargetSettings) {
    const targetToSave = this.findAccountingTargetFromCompanySettings(settings);
    targetToSave.data = { ...targetToSave.data, ...settings.data };
    this.settingsService.save();
  }

  private findAccountingTargetFromCompanySettings(target: AccountingTargetSettings): AccountingTargetSettings {
    return this.settingsService.current.accounting?.targets
      .find(v => v.id === target.id);
  }

  private isAdmin(): boolean {
    const result = !!this.sessionService.clientRoles.find(v => v === "admin");
    return result;
  }

}
